import { cva } from "class-variance-authority";
import type { ComponentPropsWithoutRef } from "react";
import { type DropzoneOptions, useDropzone } from "react-dropzone-esm";
import { classNames } from "../../utils/classNames";

export const dropzoneVariants = cva(
  "group relative overflow-hidden py-3 px-4 border rounded-xl flex items-center flex-col gap-3 justify-center transition-all duration-300",
  {
    variants: {
      variant: {
        default: "",
        warning: "text-warning",
        error: "text-error",
      },
      state: {
        default: "border-dashed hover:cursor-pointer",
        filled:
          "rounded-3xl hover:cursor-pointer hover:border-dashed hover:border transition-all duration-300",
        dragged: "border-dashed",
        draggedRejected: "border-dashed",
        disabled: "cursor-not-allowed",
      },
    },
    compoundVariants: [
      // Default
      {
        variant: "default",
        state: "default",
        className:
          "border-primary hover:border-inverse-primary hover:bg-secondary",
      },
      {
        variant: "default",
        state: "filled",
        className:
          "border-primary hover:border-inverse-primary hover:bg-secondary",
      },
      {
        variant: "default",
        state: "dragged",
        className: "border-inverse-primary bg-secondary",
      },
      {
        variant: "default",
        state: "draggedRejected",
        className: "border-error text-error",
      },
      {
        variant: "default",
        state: "disabled",
        className: "border-tertiary",
      },
      // Warning
      {
        variant: "warning",
        state: "default",
        className: "border-warning hover:bg-warning-soft/20",
      },
      {
        variant: "warning",
        state: "filled",
        className: "border-warning hover:bg-warning-soft/20",
      },
      {
        variant: "warning",
        state: "dragged",
        className: "border-warning bg-warning-soft/20",
      },
      {
        variant: "warning",
        state: "draggedRejected",
        className: "border-error",
      },
      {
        variant: "warning",
        state: "disabled",
        className: "border-warning/50",
      },
      // Error
      {
        variant: "error",
        state: "default",
        className: "border-error hover:bg-error",
      },
    ],
    defaultVariants: {
      variant: "default",
      state: "default",
    },
  },
);
export interface IDropzoneProps extends ComponentPropsWithoutRef<"input"> {
  options?: DropzoneOptions;
  variant?: "default" | "warning" | "error";
  isFilled?: boolean;
}

type DropState =
  | "default"
  | "filled"
  | "dragged"
  | "draggedRejected"
  | "disabled";

export function Dropzone({
  children,
  className,
  options = {},
  style,
  variant = "default",
  isFilled = false,
  ...props
}: IDropzoneProps) {
  const { getRootProps, getInputProps, isDragActive, isDragAccept } =
    useDropzone(options);

  const rules: Array<[boolean, DropState]> = [
    [options.disabled === true, "disabled"],
    [isDragActive && isDragAccept && !options.disabled, "dragged"],
    [isDragActive && !isDragAccept && !options.disabled, "draggedRejected"],
    [isFilled && !options.disabled && !isDragActive, "filled"],
    [!options.disabled, "default"],
  ];

  const state: DropState = rules.find(([cond]) => cond)?.[1] ?? "default";

  return (
    <div
      {...getRootProps()}
      data-slot="control"
      className={classNames(dropzoneVariants({ variant, state }), className)}
      style={style}
    >
      <input {...props} {...getInputProps()} />
      {children}
    </div>
  );
}
